import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/client.js';
import { AppError } from '../utils/error.js';

const SETTINGS_COL = '_settings';
const AUTH_DOC = 'auth';

/**
 * ドメイン形式の検証（RFC1035 簡易版）。サブドメイン・TLD 含めて [a-z0-9.-] のみ。
 * IDN は punycode (`xn--...`) で登録すること（生 Unicode は不可）。
 *
 * UI 側でも同じ regex で即時バリデーションする想定で export している。
 * Rules 側にも `_settings/{docId}` の write rule で件数上限と整合させること（MAX_DOMAINS）。
 */
export const DOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;
export const MAX_DOMAINS = 20;

export interface AuthSettings {
  /**
   * サインアップ時に許可するメールドメインの配列。
   * 完全一致判定（サブドメインを許可したい場合は明示登録）。
   * 例: ['kobegakuin.ac.jp', 'j.kobegakuin.ac.jp']
   */
  allowedEmailDomains: string[];
  /**
   * `_settings/auth` ドキュメントが Firestore に存在するか。
   * UI で「未初期化」と「空配列保存済」を区別するために使用。
   */
  initialized: boolean;
}

/**
 * `_settings/auth` ドキュメントを取得する。
 *
 * 未認証時は Rules で permission-denied になるため、呼び出しは認証直後（bootstrapUser 内）か
 * 認証済みコンテキストに限定する。bootstrapUser から呼ばれる経路では fail-closed のために
 * 未初期化時は AppError をスローするが、管理画面など UI 側は initialized=false を扱える
 * `getAuthSettingsOrEmpty()` を使うこと。
 */
export async function getAuthSettings(): Promise<AuthSettings> {
  const snap = await getDoc(doc(db, `${SETTINGS_COL}/${AUTH_DOC}`));
  if (!snap.exists()) {
    throw new AppError({
      code: 'app/settings-not-initialized',
      userMessage: 'システム設定が未初期化です。管理者にご連絡ください。',
    });
  }
  const data = snap.data();
  const list = data['allowedEmailDomains'];
  if (!Array.isArray(list) || list.some((v) => typeof v !== 'string')) {
    throw new AppError({
      code: 'app/settings-malformed',
      userMessage: 'システム設定に不整合があります。管理者にご連絡ください。',
    });
  }
  return { allowedEmailDomains: list as string[], initialized: true };
}

/**
 * 管理画面用のローダ。`_settings/auth` 不在時は initialized=false で空配列を返す。
 * permission-denied 等のその他エラーはそのままスローする。
 */
export async function getAuthSettingsOrEmpty(): Promise<AuthSettings> {
  try {
    return await getAuthSettings();
  } catch (err) {
    if (err instanceof AppError && err.code === 'app/settings-not-initialized') {
      return { allowedEmailDomains: [], initialized: false };
    }
    throw err;
  }
}

/**
 * 許可ドメインリストを更新する。Rules で書込は管理者のみ許可。
 *
 * 入力は normalize（trim + 小文字化）し、形式不正・重複は事前に弾く。
 * `merge: true` で書き込むので、将来 `_settings/auth` に他フィールドが追加されても消さない。
 */
export async function updateAllowedDomains(domains: string[]): Promise<string[]> {
  const normalized = Array.from(
    new Set(
      domains
        .map((d) => d.trim().toLowerCase())
        .filter((d) => d.length > 0),
    ),
  );
  if (normalized.length === 0) {
    throw new AppError({
      code: 'app/settings-empty',
      userMessage: '許可ドメインを 1 件以上指定してください。',
    });
  }
  if (normalized.length > MAX_DOMAINS) {
    throw new AppError({
      code: 'app/settings-too-many',
      userMessage: `許可ドメインは ${MAX_DOMAINS} 件までです。`,
    });
  }
  const invalid = normalized.find((d) => !DOMAIN_PATTERN.test(d));
  if (invalid) {
    throw new AppError({
      code: 'app/settings-malformed-domain',
      userMessage: `ドメイン形式が不正です: ${invalid}`,
    });
  }
  await setDoc(
    doc(db, `${SETTINGS_COL}/${AUTH_DOC}`),
    {
      allowedEmailDomains: normalized,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  return normalized;
}
